import { IonCol, IonFabButton, IonGrid, IonIcon, IonRow, IonText } from '@ionic/react';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { VariabilityGraphLegenda } from '@/pages/ReportV2/components/CustomGraphComponents';
import { VariabilityOverviewGraph } from '@/pages/ReportV2/components/Graphs';
import { VariabilityModalContent } from '@/pages/ReportV2/components/Modals';
import styles from '@/pages/ReportV2/Styles/Reports.module.css';

import { Card } from '@/components/Cards';
import { Icon } from '@/components/Icon';
import { ReportModal } from '@/components/Modal/ReportModal';
import { toast } from '@/components/Toaster/Toast';

import ToggleLeft from '@/assets/icons/toggle-left.svg';

import { RunVariabilityData } from '@/models/reportV2.model';

type RunVariabilityReportTabProps = {
  tabData: RunVariabilityData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const RunVariabilityReportTab = ({ tabData, setFloatingButtons }: RunVariabilityReportTabProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);

  const [showMoreInfoModal, setShowMoreInfoModal] = useState({
    show: false,
    joint: '',
  });

  const seriesHip = React.useMemo(() => {
    return showAllStrides ? tabData.allStrides.Pelvis : tabData.mean.Pelvis;
  }, [tabData, showAllStrides]);
  const seriesKnee = React.useMemo(() => {
    return showAllStrides ? tabData.allStrides.Knee : tabData.mean.Knee;
  }, [tabData, showAllStrides]);
  const seriesAnkle = React.useMemo(() => {
    return showAllStrides ? tabData.allStrides.Ankle : tabData.mean.Ankle;
  }, [tabData, showAllStrides]);

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesVariability', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const showInfoToast = () =>
    toast({
      icon: 'info',
      title: 'Enlarge graphs',
      description: 'Do this by clicking / tapping on the chart',
      duration: 3000,
    });

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={ToggleLeft}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={showInfoToast} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <ReportModal
        isOpen={showMoreInfoModal.show}
        onDidDismiss={() => setShowMoreInfoModal({ show: false, joint: '' })}
        setShowInfo={() => setShowMoreInfoModal({ show: false, joint: '' })}>
        <IonGrid className='' style={{ height: '100%' }}>
          {showMoreInfoModal.joint === 'Hip' && (
            <VariabilityModalContent
              annotations={true}
              coordinativeVariabilityData_Left={tabData.modalData.Pelvis.Left.coordinativeVariabilityData}
              coordinativeVariabilityData_Right={tabData.modalData.Pelvis.Right.coordinativeVariabilityData}
              crp_Left={tabData.modalData.Pelvis.Left.crpData}
              crp_Right={tabData.modalData.Pelvis.Right.crpData}
              graphAnnotations_Left={tabData.graphAnnotationsHip_Left}
              graphAnnotations_Right={tabData.graphAnnotationsHip_Right}
              setShowMoreInfoModal={setShowMoreInfoModal}
            />
          )}
          {showMoreInfoModal.joint === 'Knee' && (
            <VariabilityModalContent
              annotations={true}
              coordinativeVariabilityData_Left={tabData.modalData.Knee.Left.coordinativeVariabilityData}
              coordinativeVariabilityData_Right={tabData.modalData.Knee.Right.coordinativeVariabilityData}
              crp_Left={tabData.modalData.Knee.Left.crpData}
              crp_Right={tabData.modalData.Knee.Right.crpData}
              graphAnnotations_Left={tabData.graphAnnotations_Left}
              graphAnnotations_Right={tabData.graphAnnotations_Right}
              setShowMoreInfoModal={setShowMoreInfoModal}
            />
          )}
          {showMoreInfoModal.joint === 'Ankle' && (
            <VariabilityModalContent
              annotations={true}
              coordinativeVariabilityData_Left={tabData.modalData.Ankle.Left.coordinativeVariabilityData}
              coordinativeVariabilityData_Right={tabData.modalData.Ankle.Right.coordinativeVariabilityData}
              crp_Left={tabData.modalData.Ankle.Left.crpData}
              crp_Right={tabData.modalData.Ankle.Right.crpData}
              graphAnnotations_Left={tabData.graphAnnotations_Left}
              graphAnnotations_Right={tabData.graphAnnotations_Right}
              setShowMoreInfoModal={setShowMoreInfoModal}
            />
          )}
        </IonGrid>
      </ReportModal>

      <IonRow className={styles.variabilityRow}>
        <IonCol className='ion-align-self-stretch' size='12'>
          <Card
            onClick={() => setShowMoreInfoModal({ show: true, joint: 'Hip' })}
            style={{ height: 'fit-content', margin: 0 }}>
            <IonRow
              className='ion-no-padding ion-align-items-center ion-justify-content-evenly'
              style={{ height: '100%' }}>
              <IonCol className='ion-align-self-stretch' size='2'>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '25%' }}>
                  <IonCol className='ion-no-padding ' size='12'>
                    <p className={styles.variabilityTitle}>Hip</p>
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '75%' }}>
                  <VariabilityGraphLegenda legend={tabData.pelvisLegend} />
                </IonRow>
              </IonCol>
              <IonCol className='ion-no-padding ion-align-items-center ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesHip.Left.coordinativeVariabilityData}
                  crpData={seriesHip.Left.crpData}
                  graphAnnotations={tabData.graphAnnotationsHip_Left}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
              <IonCol className='ion-no-padding ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesHip.Right.coordinativeVariabilityData}
                  crpData={seriesHip.Right.crpData}
                  graphAnnotations={tabData.graphAnnotationsHip_Right}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
            </IonRow>
          </Card>
        </IonCol>
      </IonRow>

      <IonRow className={styles.variabilityRow}>
        <IonCol className='ion-align-self-stretch' size='12'>
          <Card
            onClick={() => setShowMoreInfoModal({ show: true, joint: 'Knee' })}
            style={{ height: 'fit-content', margin: 0 }}>
            <IonRow
              className='ion-no-padding ion-align-items-center ion-justify-content-evenly'
              style={{ height: '100%' }}>
              <IonCol className='ion-align-self-stretch' size='2'>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '25%' }}>
                  <IonCol className='ion-no-padding' size='12'>
                    <p className={styles.variabilityTitle}>Knee</p>
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '75%' }}>
                  <VariabilityGraphLegenda legend={tabData.kneeLegend} />
                </IonRow>
              </IonCol>
              <IonCol className='ion-no-padding ion-align-items-center ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesKnee.Left.coordinativeVariabilityData}
                  crpData={seriesKnee.Left.crpData}
                  graphAnnotations={tabData.graphAnnotations_Left}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
              <IonCol className='ion-no-padding ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesKnee.Right.coordinativeVariabilityData}
                  crpData={seriesKnee.Right.crpData}
                  graphAnnotations={tabData.graphAnnotations_Right}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
            </IonRow>
          </Card>
        </IonCol>
      </IonRow>

      <IonRow className={styles.variabilityRow}>
        <IonCol className='ion-align-self-stretch' size='12'>
          <Card
            onClick={() => setShowMoreInfoModal({ show: true, joint: 'Ankle' })}
            style={{ height: 'fit-content', margin: 0 }}>
            <IonRow
              className='ion-no-padding ion-align-items-center ion-justify-content-evenly'
              style={{ height: '100%' }}>
              <IonCol className='ion-align-self-stretch' size='2'>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '25%' }}>
                  <IonCol className='ion-no-padding' size='12'>
                    <p className={styles.variabilityTitle}>Ankle</p>
                  </IonCol>
                </IonRow>
                <IonRow className='ion-align-items-center ion-justify-content-center' style={{ height: '75%' }}>
                  <VariabilityGraphLegenda legend={tabData.ankleLegend} />
                </IonRow>
              </IonCol>
              <IonCol className='ion-no-padding ion-align-items-center ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesAnkle.Left.coordinativeVariabilityData}
                  crpData={seriesAnkle.Left.crpData}
                  graphAnnotations={tabData.graphAnnotations_Left}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
              <IonCol className='ion-no-padding ion-align-self-stretch' size='5'>
                <VariabilityOverviewGraph
                  coordinativeVariabilityData={seriesAnkle.Right.coordinativeVariabilityData}
                  crpData={seriesAnkle.Right.crpData}
                  graphAnnotations={tabData.graphAnnotations_Right}
                  strokeWidth={showAllStrides ? 1 : 2}
                />
              </IonCol>
            </IonRow>
          </Card>
        </IonCol>
      </IonRow>
    </>
  );
};
